import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Table } from "@opendate-app/setlist";
import ActionsMenu from "../../shared/components/ActionsMenu";
import HoldModal from "../../ConfirmTicketing/components/HoldModal";
import HoldsHeader from "../../ConfirmTicketing/components/HoldsHeader";
import GeneralAdmissionHoldModal from "../../ConfirmTicketing/components/GeneralAdmissionHoldModal";
import { HoldsKillsChartView } from "../../ConfirmTicketing/components/HoldsKillsChartView";
import ReleaseOffsetField, { releaseOffsetLabel } from "./ReleaseOffsetField";
import { chartShape, contextFromEvent, headerEvents } from "./chartHeaders";
import { useChartPlanning } from "./usePlanningEvent";
import { blankTicketType, chartContextOf, takenSeats } from "../scheduleBuilder";
import { toastError } from "../../../shared/toastHelper";

const newId = () => `series-hold-${blankTicketType().id}`;

const HoldsTab = ({ seated, charts, holds, kills = [], ticketTypeNames, timedEntry, onChange }) => {
  const [editingId, setEditingId] = useState(null);
  const [adding, setAdding] = useState(false);
  const { chart, select, planning } = useChartPlanning(charts);
  const onChart = chart.context !== null || seated;
  const shown = holds.filter((hold) => chartContextOf(hold) === chart.context);
  const editing = shown.find((hold) => hold.id === editingId) || null;
  const initial = useMemo(() => editing && { ...editing }, [editing]);
  const open = adding || Boolean(editing);
  const chartOpen = onChart && Boolean(chart.open);

  // The confirm shows the chart under its holds; the series opens its planning event to do the same.
  useEffect(() => {
    if (chartOpen) planning.load();
  }, [chartOpen, chart.context]);

  const close = () => {
    setAdding(false);
    setEditingId(null);
  };
  const save = (draft) => {
    onChange(
      editing
        ? holds.map((hold) => (hold.id === editing.id ? { ...hold, ...draft, id: hold.id } : hold))
        : [...holds, { ...draft, id: newId(), ...(chart.context && { chartContext: chart.context }) }],
    );
    close();
  };
  const begin = (id) => {
    if (onChart) planning.load();
    id ? setEditingId(id) : setAdding(true);
  };
  const switchChart = (context) => {
    close();
    select(context);
  };

  return (
    <div className="series-tab-panel">
      <HoldsHeader
        events={headerEvents(charts, seated)}
        selectedEvent={{ id: chart.context || "admission" }}
        onSelectEvent={(value) => switchChart(contextFromEvent(value))}
        onClickAdd={() => begin(null)}
        addDisabled={onChart ? !chart.open : ticketTypeNames.length === 0}
        subtitle={onChart
          ? "Hold seats back from sale on every show of this type, like sponsor or band seats."
          : "Hold tickets back from sale on every show of this type, like comps or a group block."}
      />
      {onChart && !chart.open && (
        <p className="series-edit-hint" data-testid="holds-need-chart">Choose a seating chart on Admission to hold seats.</p>
      )}
      {planning.error && <p className="series-schedule-error">{planning.error}</p>}

      <Table
        columns={[
          { key: "name", header: "Name", primary: true, render: (hold) => hold.name },
          onChart
            ? { key: "seats", header: "Seats", render: (hold) => (hold.objects || []).length }
            : { key: "ticketType", header: "Ticket Type", render: (hold) => `${hold.quantity} × ${hold.ticketTypeName}` },
          { key: "release", header: "Release", render: (hold) => releaseOffsetLabel(hold.releaseOffsetMinutes) },
          {
            key: "actions",
            header: "",
            align: "right",
            width: "56px",
            render: (hold) => (
              <ActionsMenu
                actions={[
                  { label: "Edit", icon: "fal fa-pencil", onClick: () => begin(hold.id) },
                  {
                    label: "Delete",
                    icon: "fal fa-trash-alt",
                    danger: true,
                    onClick: () => onChange(holds.filter((candidate) => candidate.id !== hold.id)),
                  },
                ]}
              />
            ),
          },
        ]}
        data={shown}
        getRowKey={(hold) => hold.id}
        empty="No holds yet."
      />
      {chartOpen && planning.event && (
        <HoldsKillsChartView
          selectedEvent={{ seatsio_event_id: planning.event.eventKey }}
          holds={shown}
          kills={kills.filter((kill) => chartContextOf(kill) === chart.context)}
          seatsIOPublicKey={planning.event.publicKey || ""}
        />
      )}

      {onChart ? (
        planning.event && (
          <HoldModal
            key={chart.context || "admission"}
            open={open}
            onClose={close}
            title={editing ? "Edit Hold" : "Add Hold"}
            seatsIOPublicKey={planning.event.publicKey || ""}
            selectedEvent={{ seatsio_event_id: planning.event.eventKey }}
            initialHold={initial && { name: initial.name, objects: initial.objects, color: initial.color, release_offset_minutes: initial.releaseOffsetMinutes }}
            takenObjects={takenSeats({ holds, kills, exceptId: editing && editing.id, chartContext: chart.context })}
            renderRelease={(value, setValue) => <ReleaseOffsetField id="seat-hold-release" value={value} onChange={setValue} />}
            onSubmit={(hold) => {
              const name = (hold.name || "").trim();
              if (shown.some((other) => other.id !== (editing && editing.id) && (other.name || "").trim() === name)) {
                toastError(`Another hold on this chart is already named “${name}”.`);
                return Promise.resolve();
              }
              save({ name, objects: hold.objects, color: hold.color, releaseOffsetMinutes: hold.release_offset_minutes });
              return Promise.resolve();
            }}
          />
        )
      ) : (
        <GeneralAdmissionHoldModal
          open={open}
          onClose={close}
          title={editing ? "Edit Hold" : "Add Hold"}
          ticketTypes={ticketTypeNames.map((name) => ({ id: name, name }))}
          initialHold={initial && {
            id: initial.id,
            name: initial.name,
            release_offset_minutes: initial.releaseOffsetMinutes,
            general_admission_hold_items: [{ holdable_id: initial.ticketTypeName, quantity: initial.quantity }],
          }}
          quantityHint={timedEntry ? "This many are held in every entry time." : undefined}
          allowMultipleKinds={false}
          renderRelease={(value, setValue) => <ReleaseOffsetField id="hold-release" value={value} onChange={setValue} />}
          onSubmit={(payload) => {
            const [item] = payload.general_admission_hold_items_attributes;
            save({
              name: payload.name.trim(),
              ticketTypeName: item.holdable_id,
              quantity: String(item.quantity),
              releaseOffsetMinutes: payload.release_offset_minutes,
            });
            return Promise.resolve();
          }}
        />
      )}
    </div>
  );
};

HoldsTab.propTypes = {
  seated: PropTypes.bool.isRequired,
  charts: PropTypes.arrayOf(chartShape).isRequired,
  holds: PropTypes.arrayOf(PropTypes.object).isRequired,
  kills: PropTypes.arrayOf(PropTypes.object),
  ticketTypeNames: PropTypes.arrayOf(PropTypes.string).isRequired,
  timedEntry: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
};

export default HoldsTab;
